class MenuV2Filters {
  constructor(root = document) {
    this.root = root;
    this.root.addEventListener('click', this.handleNoopLinkClick.bind(this), true);
    this.root.addEventListener('click', this.handleClick.bind(this));
    this.resetContents(this.root);
    document.addEventListener('shopify:section:load', (event) => this.resetContents(event.target));
  }

  static isNoopUrl(url) {
    const cleanUrl = (url || '').split('?')[0].split('#')[0].replace(/\/$/, '').toLowerCase();
    return cleanUrl === 'https://shop.polymaker.com' || cleanUrl === 'http://shop.polymaker.com';
  }

  static resetContent(content) {
    if (!content) return;

    const firstFilter = content.querySelector('.menu-v2-filter[data-menu-v2-target]:not(:disabled):not([aria-disabled="true"])');
    const targetSet = firstFilter ? firstFilter.dataset.menuV2Target : 'all';

    content.querySelectorAll('.menu-v2-filter').forEach((filterButton) => {
      const isActive = filterButton === firstFilter;
      filterButton.classList.toggle('is-active', isActive);
      filterButton.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });

    content.querySelectorAll('.menu-v2-product-grid[data-menu-v2-product-set]').forEach((grid) => {
      grid.hidden = grid.dataset.menuV2ProductSet !== targetSet;
    });
  }

  resetContents(scope) {
    scope.querySelectorAll('.main-menu-v2 .menu-v2__content').forEach(MenuV2Filters.resetContent);
  }

  handleNoopLinkClick(event) {
    const noopLink = event.target.closest('.main-menu-v2 a[href]');
    if (noopLink && MenuV2Filters.isNoopUrl(noopLink.getAttribute('href'))) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  handleClick(event) {
    const noopLink = event.target.closest('.main-menu-v2 a[href]');
    if (noopLink && MenuV2Filters.isNoopUrl(noopLink.getAttribute('href'))) {
      event.preventDefault();
      return;
    }

    const button = event.target.closest('.menu-v2-filter');
    if (!button) return;

    event.preventDefault();

    if (button.disabled || button.getAttribute('aria-disabled') === 'true') return;

    const content = button.closest('.menu-v2__content');
    if (!content) return;

    const targetSet = button.dataset.menuV2Target || 'all';

    content.querySelectorAll('.menu-v2-filter').forEach((filterButton) => {
      const isActive = filterButton === button;
      filterButton.classList.toggle('is-active', isActive);
      filterButton.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });

    content.querySelectorAll('.menu-v2-product-grid[data-menu-v2-product-set]').forEach((grid) => {
      grid.hidden = grid.dataset.menuV2ProductSet !== targetSet;
    });

    content.dispatchEvent(new CustomEvent('menu-v2:content-change', {
      bubbles: true,
      detail: {
        panel: content.closest('.menu-v2-panel')
      }
    }));
  }
}

class MenuV2Rail {
  constructor(root = document) {
    this.root = root;
    this.isMobile = window.matchMedia('(max-width: 768.98px)');
    this.pointerHistory = [];
    this.panelHeightFrames = new WeakMap();
    syncPmdtcStickyHeaderHeight();
    this.root.addEventListener('pointerover', this.handlePointerMove.bind(this));
    this.root.addEventListener('pointermove', this.handlePointerMove.bind(this));
    this.root.addEventListener('pointerout', this.handlePointerOut.bind(this));
    this.root.addEventListener('focusin', this.handleActivate.bind(this));
    this.root.addEventListener('click', this.handleActivate.bind(this));
    this.root.addEventListener('menu-v2:content-change', (event) => {
      this.syncPanelHeight(event.detail && event.detail.panel);
    });
    window.addEventListener('resize', this.syncAllPanelHeights.bind(this));
    // Closed menus are outside the first viewport. Initialize their cards only
    // when opened, including the native details toggles used by keyboard/mobile.
    this.root.addEventListener('toggle', (event) => {
      const details = event.target;
      if (!details.open || !details.matches('.main-menu-v2 .main-nav > li > details')) return;
      details.querySelectorAll('.menu-v2-panel').forEach((panel) => {
        this.initPanelMediaLoading(panel);
        this.queuePanelHeight(panel);
      });
    }, true);
    this.root.querySelectorAll('.menu-v2-panel').forEach((panel) => {
      if (panel.closest('details')?.open) this.initPanelMediaLoading(panel);
    });
    this.syncAllPanelHeights();
  }

  initPanelMediaLoading(panel) {
    if (panel.dataset.menuV2MediaReady === 'true') return;
    panel.dataset.menuV2MediaReady = 'true';
    // Links and titles are usable immediately; slow/broken images must not
    // keep the menu blank while an image readiness timer runs.
    panel.classList.remove('is-loading');
    panel.querySelectorAll('.menu-v2-product-card, .menu-v2-banner-card').forEach((card) => {
      card.classList.add('is-media-ready');
    });
    panel.querySelectorAll('img').forEach((image) => {
      if (image.complete) return;
      image.addEventListener('load', () => this.queuePanelHeight(panel), { once: true });
      image.addEventListener('error', () => this.queuePanelHeight(panel), { once: true });
    });
    this.queuePanelHeight(panel);
  }

  queuePanelHeight(panel) {
    if (!panel || this.panelHeightFrames.has(panel)) return;
    const frame = window.requestAnimationFrame(() => {
      this.panelHeightFrames.delete(panel);
      this.syncPanelHeight(panel);
    });
    this.panelHeightFrames.set(panel, frame);
  }

  cancelHover() {
    window.clearTimeout(this.hoverTimer);
    this.pendingItem = null;
  }

  handlePointerMove(event) {
    if (this.isMobile.matches || event.pointerType !== 'mouse') return;

    const panel = event.target.closest('.menu-v2-panel');
    if (panel !== this.pointerPanel) {
      this.cancelHover();
      this.pointerHistory = [];
      this.pointerPanel = panel;
    }
    if (!panel) return;

    const now = performance.now();
    const point = { x: event.clientX, y: event.clientY, time: now };
    this.pointerHistory = this.pointerHistory.filter((sample) => now - sample.time < 150);
    const previous = this.pointerHistory[0];
    this.pointerHistory.push(point);

    const link = event.target.closest('.menu-v2__rail-link');
    const railItem = link && link.closest('.menu-v2__rail-item');
    if (!railItem || railItem.classList.contains('is-active')) {
      this.cancelHover();
      return;
    }

    if (this.pendingItem !== railItem) {
      this.cancelHover();
      this.pendingItem = railItem;
      this.hoverStartedAt = now;
    }

    const content = panel.querySelector('.menu-v2__rail-item.is-active > .menu-v2__content');
    const delay = this.isMovingTowardContent(previous, point, content) ? 300 : 120;
    window.clearTimeout(this.hoverTimer);
    this.hoverTimer = window.setTimeout(() => {
      this.cancelHover();
      if (!this.isMobile.matches && link.isConnected && link.matches(':hover')) {
        this.activateItem(railItem);
      }
    }, Math.max(0, delay - (now - this.hoverStartedAt)));
  }

  isMovingTowardContent(previous, point, content) {
    if (!previous || !content || point.x <= previous.x) return false;

    const bounds = content.getBoundingClientRect();
    if (bounds.height === 0 || point.x >= bounds.left) return false;

    // Project the pointer's direction onto the content edge to allow diagonal travel.
    const projectedY = previous.y
      + (point.y - previous.y) * (bounds.left - previous.x) / (point.x - previous.x);
    return projectedY >= bounds.top - 24 && projectedY <= bounds.bottom + 24;
  }

  handlePointerOut(event) {
    if (event.pointerType !== 'mouse' || !this.pointerPanel) return;

    if (!this.pointerPanel.contains(event.relatedTarget)) {
      this.cancelHover();
      this.pointerHistory = [];
      this.pointerPanel = null;
    } else if (this.pendingItem && !this.pendingItem.contains(event.relatedTarget)) {
      this.cancelHover();
    }
  }

  handleActivate(event) {
    if (this.isMobile.matches) return;

    const railItem = event.target.closest('.menu-v2__rail-item');
    if (!railItem) return;

    this.cancelHover();
    this.pointerHistory = [];
    this.activateItem(railItem);
  }

  activateItem(railItem) {
    const rail = railItem.closest('.menu-v2__rail');
    if (!rail) return;

    const wasActive = railItem.classList.contains('is-active');

    rail.querySelectorAll('.menu-v2__rail-item').forEach((item) => {
      item.classList.toggle('is-active', item === railItem);
    });

    if (!wasActive) {
      MenuV2Filters.resetContent(railItem.querySelector('.menu-v2__content'));
    }

    this.syncPanelHeight(railItem.closest('.menu-v2-panel'));
  }

  syncAllPanelHeights() {
    this.root.querySelectorAll('.menu-v2-panel').forEach((panel) => {
      this.syncPanelHeight(panel);
    });
  }

  syncPanelHeight(panel) {
    if (!panel) return;
    if (panel.closest('details')?.open === false) return;

    if (window.matchMedia('(max-width: 768.98px)').matches) {
      panel.style.removeProperty('--menu-v2-panel-height');
      panel.style.removeProperty('--menu-v2-fixed-offset');
      return;
    }

    const rail = panel.querySelector('.menu-v2__rail');
    const activeContent = panel.querySelector('.menu-v2__rail-item.is-active > .menu-v2__content, .menu-v2__content--static');
    const filters = activeContent && activeContent.querySelector('.menu-v2-filters');
    const filterStyles = filters ? window.getComputedStyle(filters) : null;
    const fixedOffset = filters
      ? filters.offsetHeight + Number.parseFloat(filterStyles.marginBottom || 0)
      : 0;
    panel.style.setProperty('--menu-v2-fixed-offset', `${Math.ceil(fixedOffset)}px`);

    const railHeight = rail ? rail.scrollHeight : 0;
    const contentHeight = activeContent ? activeContent.scrollHeight : 0;
    const panelHeight = Math.max(railHeight, contentHeight);

    panel.style.setProperty('--menu-v2-panel-height', `${Math.ceil(panelHeight)}px`);
  }
}

class MenuV2MobileDisclosure {
  constructor(root = document) {
    this.root = root;
    this.isMobile = window.matchMedia('(max-width: 768.98px)');
    this.handleToggle = this.handleToggle.bind(this);

    this.bind(this.root);
    document.addEventListener('shopify:section:load', (event) => this.bind(event.target));
  }

  bind(scope) {
    scope.querySelectorAll(
      '.main-menu-v2 .main-menu__disclosure, .main-menu-v2 .main-nav > li > details, .main-menu-v2 .menu-v2__mobile-branch, .main-menu-v2 .menu-v2__mobile-grandchild'
    ).forEach((details) => {
      if (details.dataset.menuV2MobileBound === 'true') return;

      details.addEventListener('toggle', this.handleToggle);
      details.dataset.menuV2MobileBound = 'true';
    });
  }

  closeNested(details) {
    details.querySelectorAll('.menu-v2__mobile-branch[open], .menu-v2__mobile-grandchild[open]').forEach((childDetails) => {
      childDetails.open = false;
    });
  }

  handleToggle(event) {
    if (!this.isMobile.matches) return;
    if (event.target !== event.currentTarget) return;

    const details = event.currentTarget;

    if (details.classList.contains('menu-v2__mobile-grandchild')) {
      if (!details.open) return;

      const panel = details.closest('.menu-v2-panel');
      if (!panel) return;
      panel.querySelectorAll('.menu-v2__mobile-grandchild[open]').forEach((grandchild) => {
        if (grandchild !== details) grandchild.open = false;
      });
      return;
    }

    if (details.classList.contains('menu-v2__mobile-branch') && details.open) {
      const firstGrandchild = details.querySelector('.menu-v2__mobile-grandchild');
      if (firstGrandchild) firstGrandchild.open = true;
      return;
    }

    this.closeNested(details);
  }
}

class PmdtcHeaderSearch {
  constructor(header) {
    this.header = header;
    this.search = header.querySelector('.pmdtc-header__search');
    this.input = this.search && this.search.querySelector('.js-search-input');
    this.predictiveSearch = this.search && this.search.querySelector('predictive-search');
    this.triggers = header.querySelectorAll('.pmdtc-header__search-trigger');

    if (!this.search) return;

    this.triggers.forEach((trigger) => {
      trigger.addEventListener('click', this.handleTriggerClick.bind(this));
    });
    this.search.addEventListener('focusin', () => this.activate());
    this.search.addEventListener('focusout', () => {
      window.setTimeout(() => this.sync(), 80);
    });

    document.addEventListener('click', (event) => {
      if (this.header.contains(event.target)) return;
      this.sync();
    });

    if (this.predictiveSearch) {
      new MutationObserver(() => this.sync()).observe(this.predictiveSearch, {
        attributes: true,
        attributeFilter: ['open']
      });
    }
  }

  handleTriggerClick(event) {
    event.preventDefault();
    this.activate();
    window.requestAnimationFrame(() => {
      if (this.input) this.input.focus();
    });
  }

  activate() {
    this.header.classList.add('is-search-active');
    this.triggers.forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'true');
    });
  }

  sync() {
    const hasFocus = this.search.contains(document.activeElement);
    const isOpen = this.predictiveSearch && this.predictiveSearch.hasAttribute('open');
    const isActive = hasFocus || isOpen;
    this.header.classList.toggle('is-search-active', isActive);
    this.triggers.forEach((trigger) => {
      trigger.setAttribute('aria-expanded', isActive ? 'true' : 'false');
    });
  }
}

class PmdtcAnnouncement {
  constructor(root) {
    this.root = root;
    this.message = root.querySelector('.pmdtc-announcement__message');
    this.slides = Array.from(root.querySelectorAll('.pmdtc-announcement__message .pmdtc-announcement__text'));
    this.mobileLinks = root.querySelector('.pmdtc-announcement__mobile-links');
    this.mobileLinkSlides = Array.from(root.querySelectorAll('.pmdtc-announcement__mobile-link'));
    this.delay = Number(root.dataset.slideDelay) || 7000;
    this.messageIndex = 0;
    this.mobileLinkIndex = 0;

    this.init();
  }

  init() {
    this.initCarousel(this.message, this.slides, () => this.showNextMessage());
    this.initCarousel(this.mobileLinks, this.mobileLinkSlides, () => this.showNextMobileLink());
  }

  initCarousel(container, slides, callback) {
    if (!container || slides.length === 0) return;

    slides[0].classList.add('is-visible');

    if (slides.length < 2) return;

    container.classList.add('is-rotating');
    window.setInterval(callback, this.delay);
  }

  showNextMessage() {
    this.messageIndex = this.showNext(this.slides, this.messageIndex);
  }

  showNextMobileLink() {
    this.mobileLinkIndex = this.showNext(this.mobileLinkSlides, this.mobileLinkIndex);
  }

  showNext(slides, index) {
    if (slides.length < 2) return index;

    const current = slides[index];
    const nextIndex = (index + 1) % slides.length;
    const next = slides[nextIndex];
    current.classList.remove('is-visible');
    next.classList.add('is-visible');

    return nextIndex;
  }
}

function syncPmdtcStickyHeaderHeight() {
  const header = document.querySelector('.pmdtc-header');
  if (!header) return;

  const rect = header.getBoundingClientRect();
  const height = Math.ceil(rect.height || header.offsetHeight || 0);
  if (height <= 0) return;

  document.documentElement.style.setProperty('--pmdtc-sticky-header-height', `${height}px`);
  document.documentElement.style.setProperty('--pmdtc-menu-dropdown-top', `${Math.max(0, Math.ceil(rect.bottom))}px`);
}

function syncPmdtcMenuDropdownBeforeOpen(event) {
  if (!event.target.closest || !event.target.closest('.main-menu-v2 .js-nav-hover, .main-menu-v2 .mega-nav--menu-v2')) return;

  syncPmdtcStickyHeaderHeight();
}

if (!window.menuV2FiltersReady) {
  new MenuV2Filters();
  window.menuV2FiltersReady = true;
}

if (!window.menuV2RailReady) {
  new MenuV2Rail();
  window.menuV2RailReady = true;
}

if (!window.menuV2MobileDisclosureReady) {
  new MenuV2MobileDisclosure();
  window.menuV2MobileDisclosureReady = true;
}

if (!window.pmdtcHeaderSearchReady) {
  document.querySelectorAll('.pmdtc-header').forEach((header) => {
    new PmdtcHeaderSearch(header);
  });
  window.pmdtcHeaderSearchReady = true;
}

if (!window.pmdtcAnnouncementReady) {
  document.querySelectorAll('.pmdtc-announcement').forEach((announcement) => {
    new PmdtcAnnouncement(announcement);
  });
  window.pmdtcAnnouncementReady = true;
}

if (!window.pmdtcStickyHeaderHeightReady) {
  let stickyHeaderFrame = null;
  const updateStickyHeaderHeight = () => {
    if (stickyHeaderFrame) return;

    stickyHeaderFrame = window.requestAnimationFrame(() => {
      stickyHeaderFrame = null;
      syncPmdtcStickyHeaderHeight();
    });
  };

  syncPmdtcStickyHeaderHeight();
  window.addEventListener('load', syncPmdtcStickyHeaderHeight);
  window.addEventListener('resize', updateStickyHeaderHeight);
  window.addEventListener('scroll', updateStickyHeaderHeight, { passive: true });
  document.addEventListener('shopify:section:load', updateStickyHeaderHeight);

  if ('ResizeObserver' in window) {
    const observer = new ResizeObserver(updateStickyHeaderHeight);
    document.querySelectorAll('.pmdtc-header').forEach((header) => observer.observe(header));
  }

  window.pmdtcStickyHeaderHeightReady = true;
}

if (!window.pmdtcMenuDropdownBeforeOpenReady) {
  document.addEventListener('mouseover', syncPmdtcMenuDropdownBeforeOpen, true);
  document.addEventListener('focusin', syncPmdtcMenuDropdownBeforeOpen, true);
  document.addEventListener('touchstart', syncPmdtcMenuDropdownBeforeOpen, { capture: true, passive: true });
  window.pmdtcMenuDropdownBeforeOpenReady = true;
}

if (!window.menuV2CountrySelectorReady) {
  document.addEventListener('click', (event) => {
    const countryButton = event.target.closest('.mobile-country-option');
    if (!countryButton) return;

    const form = countryButton.closest('form');
    const countryInput = form && form.querySelector('[name="country_code"]');
    if (!form || !countryInput) return;

    event.preventDefault();
    countryInput.value = countryButton.dataset.countryCode;
    form.submit();
  });
  window.menuV2CountrySelectorReady = true;
}
